import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { communityAPI } from '../services/api';
import './ISACommunity.css';

const ISACommunity = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState('');
  const currentUser = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchPosts();
  }, [activeTab]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = activeTab !== 'all' ? { type: activeTab } : {};
      const response = await communityAPI.getPosts('ISA', params);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Error fetching ISA posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    try {
      await communityAPI.likePost(postId);
      fetchPosts();
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const openCreateModal = (type) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setCreateType(type);
    setShowCreateModal(true);
  };

  const tabs = [
    { id: 'all', label: 'All Posts', icon: '📋' },
    { id: 'ride', label: 'Rides', icon: '🚗' },
    { id: 'accommodation', label: 'Housing', icon: '🏠' },
    { id: 'whatsapp', label: 'WhatsApp', icon: '💬' },
    { id: 'event', label: 'Events', icon: '🎉' },
    { id: 'food', label: 'Food', icon: '🍛' },
  ];

  const quickActions = [
    { type: 'ride', label: 'Share Ride', icon: '🚗', color: '#4CAF50' },
    { type: 'accommodation', label: 'List Housing', icon: '🏠', color: '#2196F3' },
    { type: 'whatsapp', label: 'Share Group', icon: '💬', color: '#25D366' },
    { type: 'event', label: 'Post Event', icon: '🎉', color: '#FF9800' },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDate(dateString);
  };

  const renderPostCard = (post) => {
    const isLiked = currentUser && post.likes?.includes(currentUser.id);

    return (
      <div key={post._id} className="isa-post-card">
        <div className="post-header">
          <div className="post-author">
            <div className="author-avatar">
              {post.author?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="author-info">
              <span className="author-name">{post.author?.name || 'Anonymous'}</span>
              <span className="post-time">{formatTime(post.createdAt)}</span>
            </div>
          </div>
          <span className={`post-type-badge ${post.type}`}>
            {post.type === 'ride' && '🚗'}
            {post.type === 'accommodation' && '🏠'}
            {post.type === 'whatsapp' && '💬'}
            {post.type === 'event' && '🎉'}
            {post.type === 'food' && '🍛'}
            {post.type === 'general' && '📝'}
            {post.type.charAt(0).toUpperCase() + post.type.slice(1)}
          </span>
        </div>

        <h3 className="post-title">{post.title}</h3>
        <p className="post-description">{post.description}</p>

        {/* Ride Details */}
        {post.type === 'ride' && post.rideDetails && (
          <div className="ride-details">
            <div className="route-info">
              <div className="route-point">
                <span className="point-dot from"></span>
                <span>{post.rideDetails.from}</span>
              </div>
              <div className="route-line"></div>
              <div className="route-point">
                <span className="point-dot to"></span>
                <span>{post.rideDetails.to}</span>
              </div>
            </div>
            <div className="ride-meta">
              <span>📅 {formatDate(post.rideDetails.date)}</span>
              <span>⏰ {post.rideDetails.time}</span>
              <span>💺 {post.rideDetails.seatsAvailable} seats</span>
              {post.rideDetails.pricePerSeat && (
                <span className="price">💵 ${post.rideDetails.pricePerSeat}/seat</span>
              )}
            </div>
          </div>
        )}

        {/* Accommodation Details */}
        {post.type === 'accommodation' && post.accommodationDetails && (
          <div className="accommodation-details">
            <div className="accommodation-meta">
              <span>📍 {post.accommodationDetails.location}</span>
              <span className="rent">
                💰 ${post.accommodationDetails.rent}/{post.accommodationDetails.rentPeriod || 'month'}
              </span>
            </div>
            <div className="accommodation-tags">
              <span className="tag">{post.accommodationDetails.roomType}</span>
              {post.accommodationDetails.nearCampus && <span className="tag">Near Campus</span>}
              {post.accommodationDetails.genderPreference !== 'any' && (
                <span className="tag">{post.accommodationDetails.genderPreference} only</span>
              )}
            </div>
            {post.accommodationDetails.amenities?.length > 0 && (
              <div className="amenities">
                {post.accommodationDetails.amenities.slice(0, 4).map((amenity, idx) => (
                  <span key={idx} className="amenity-tag">{amenity}</span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* WhatsApp Group Details */}
        {post.type === 'whatsapp' && post.whatsappDetails && (
          <div className="whatsapp-details">
            <div className="whatsapp-info">
              <span className="group-name">📱 {post.whatsappDetails.groupName}</span>
              {post.whatsappDetails.memberCount && (
                <span className="member-count">👥 {post.whatsappDetails.memberCount} members</span>
              )}
              <span className="category-tag">{post.whatsappDetails.category}</span>
            </div>
            {post.whatsappDetails.groupLink && (
              <a
                href={post.whatsappDetails.groupLink}
                target="_blank"
                rel="noopener noreferrer"
                className="join-group-btn"
                onClick={(e) => e.stopPropagation()}
              >
                Join Group
              </a>
            )}
          </div>
        )}

        {/* Event Details */}
        {post.type === 'event' && post.eventDetails && (
          <div className="event-details">
            <div className="event-meta">
              <span>📅 {formatDate(post.eventDetails.date)}</span>
              <span>⏰ {post.eventDetails.time}</span>
              <span>📍 {post.eventDetails.venue}</span>
            </div>
            {post.eventDetails.isVirtual && (
              <span className="virtual-badge">🖥️ Virtual Event</span>
            )}
          </div>
        )}

        {/* Contact Info */}
        {post.contactInfo && (
          <div className="contact-section">
            {post.contactInfo.whatsapp && (
              <a
                href={`https://wa.me/${post.contactInfo.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-btn whatsapp"
                onClick={(e) => e.stopPropagation()}
              >
                WhatsApp
              </a>
            )}
            {post.contactInfo.phone && (
              <a
                href={`tel:${post.contactInfo.phone}`}
                className="contact-btn phone"
                onClick={(e) => e.stopPropagation()}
              >
                Call
              </a>
            )}
          </div>
        )}

        {/* Post Actions */}
        <div className="post-actions">
          <button
            className={`action-btn like ${isLiked ? 'liked' : ''}`}
            onClick={() => handleLike(post._id)}
          >
            {isLiked ? '❤️' : '🤍'} {post.likes?.length || 0}
          </button>
          <button className="action-btn comment">
            💬 {post.comments?.length || 0}
          </button>
          <button className="action-btn share">
            📤 Share
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="isa-community-page">
      {/* Hero Header */}
      <div className="isa-hero">
        <div className="isa-hero-content">
          <div className="isa-logo">🇮🇳</div>
          <h1>Indian Student Association</h1>
          <p>Connect with fellow Indian students • Share rides • Find housing • Join communities</p>
        </div>
        <div className="isa-hero-pattern"></div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-container">
        <div className="quick-actions">
          {quickActions.map((action) => (
            <button
              key={action.type}
              className="quick-action-btn"
              onClick={() => openCreateModal(action.type)}
              style={{ '--action-color': action.color }}
            >
              <span className="action-icon">{action.icon}</span>
              <span className="action-label">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="isa-tabs-container">
        <div className="isa-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`isa-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Posts Feed */}
      <div className="isa-feed">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <h3>No posts yet</h3>
            <p>Be the first to share something with the ISA community!</p>
            <button
              className="create-first-btn"
              onClick={() => openCreateModal('general')}
            >
              Create Post
            </button>
          </div>
        ) : (
          <div className="posts-list">
            {posts.map(renderPostCard)}
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      <button
        className="fab-create"
        onClick={() => openCreateModal('general')}
      >
        <span>+</span>
      </button>

      {/* Create Post Modal */}
      {showCreateModal && (
        <CreatePostModal
          type={createType}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchPosts();
          }}
        />
      )}
    </div>
  );
};

// Create Post Modal Component
const CreatePostModal = ({ type, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    type: type || 'general',
    title: '',
    description: '',
    rideDetails: {
      from: '',
      to: '',
      date: '',
      time: '',
      seatsAvailable: 1,
      pricePerSeat: ''
    },
    accommodationDetails: {
      location: '',
      rent: '',
      rentPeriod: 'monthly',
      roomType: 'private',
      genderPreference: 'any',
      amenities: []
    },
    whatsappDetails: {
      groupName: '',
      groupLink: '',
      memberCount: '',
      category: 'general'
    },
    contactInfo: {
      phone: '',
      whatsapp: '',
      preferredContact: 'chat'
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e, nested = null) => {
    const { name, value } = e.target;
    if (nested) {
      setFormData(prev => ({
        ...prev,
        [nested]: {
          ...prev[nested],
          [name]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        community: 'ISA',
        type: formData.type,
        title: formData.title,
        description: formData.description,
        contactInfo: formData.contactInfo
      };

      if (formData.type === 'ride') {
        payload.rideDetails = formData.rideDetails;
      } else if (formData.type === 'accommodation') {
        payload.accommodationDetails = formData.accommodationDetails;
      } else if (formData.type === 'whatsapp') {
        payload.whatsappDetails = formData.whatsappDetails;
      }

      await communityAPI.createPost(payload);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-post-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {formData.type === 'ride' && '🚗 Share a Ride'}
            {formData.type === 'accommodation' && '🏠 List Housing'}
            {formData.type === 'whatsapp' && '💬 Share WhatsApp Group'}
            {formData.type === 'event' && '🎉 Post Event'}
            {formData.type === 'general' && '📝 Create Post'}
          </h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Post Type Selector */}
          <div className="form-group">
            <label>Post Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={(e) => handleChange(e)}
            >
              <option value="general">General Post</option>
              <option value="ride">Ride Share</option>
              <option value="accommodation">Housing</option>
              <option value="whatsapp">WhatsApp Group</option>
              <option value="event">Event</option>
              <option value="food">Food/Restaurant</option>
            </select>
          </div>

          {/* Common Fields */}
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={(e) => handleChange(e)}
              placeholder="Enter a title..."
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={(e) => handleChange(e)}
              placeholder="Add details..."
              rows={3}
              required
            />
          </div>

          {/* Ride-specific fields */}
          {formData.type === 'ride' && (
            <div className="type-specific-fields">
              <div className="form-row">
                <div className="form-group">
                  <label>From</label>
                  <input
                    type="text"
                    name="from"
                    value={formData.rideDetails.from}
                    onChange={(e) => handleChange(e, 'rideDetails')}
                    placeholder="Pickup location"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>To</label>
                  <input
                    type="text"
                    name="to"
                    value={formData.rideDetails.to}
                    onChange={(e) => handleChange(e, 'rideDetails')}
                    placeholder="Destination"
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.rideDetails.date}
                    onChange={(e) => handleChange(e, 'rideDetails')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Time</label>
                  <input
                    type="time"
                    name="time"
                    value={formData.rideDetails.time}
                    onChange={(e) => handleChange(e, 'rideDetails')}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Seats Available</label>
                  <input
                    type="number"
                    name="seatsAvailable"
                    min="1"
                    max="6"
                    value={formData.rideDetails.seatsAvailable}
                    onChange={(e) => handleChange(e, 'rideDetails')}
                  />
                </div>
                <div className="form-group">
                  <label>Price per Seat ($)</label>
                  <input
                    type="number"
                    name="pricePerSeat"
                    min="0"
                    value={formData.rideDetails.pricePerSeat}
                    onChange={(e) => handleChange(e, 'rideDetails')}
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Accommodation-specific fields */}
          {formData.type === 'accommodation' && (
            <div className="type-specific-fields">
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.accommodationDetails.location}
                  onChange={(e) => handleChange(e, 'accommodationDetails')}
                  placeholder="Address or area"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Rent ($)</label>
                  <input
                    type="number"
                    name="rent"
                    min="0"
                    value={formData.accommodationDetails.rent}
                    onChange={(e) => handleChange(e, 'accommodationDetails')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Period</label>
                  <select
                    name="rentPeriod"
                    value={formData.accommodationDetails.rentPeriod}
                    onChange={(e) => handleChange(e, 'accommodationDetails')}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="semester">Semester</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Room Type</label>
                  <select
                    name="roomType"
                    value={formData.accommodationDetails.roomType}
                    onChange={(e) => handleChange(e, 'accommodationDetails')}
                  >
                    <option value="private">Private Room</option>
                    <option value="shared">Shared Room</option>
                    <option value="studio">Studio</option>
                    <option value="apartment">Full Apartment</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Gender Preference</label>
                  <select
                    name="genderPreference"
                    value={formData.accommodationDetails.genderPreference}
                    onChange={(e) => handleChange(e, 'accommodationDetails')}
                  >
                    <option value="any">Any</option>
                    <option value="male">Male Only</option>
                    <option value="female">Female Only</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* WhatsApp-specific fields */}
          {formData.type === 'whatsapp' && (
            <div className="type-specific-fields">
              <div className="form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  name="groupName"
                  value={formData.whatsappDetails.groupName}
                  onChange={(e) => handleChange(e, 'whatsappDetails')}
                  placeholder="WhatsApp group name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Group Link</label>
                <input
                  type="url"
                  name="groupLink"
                  value={formData.whatsappDetails.groupLink}
                  onChange={(e) => handleChange(e, 'whatsappDetails')}
                  placeholder="https://chat.whatsapp.com/..."
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Member Count</label>
                  <input
                    type="number"
                    name="memberCount"
                    value={formData.whatsappDetails.memberCount}
                    onChange={(e) => handleChange(e, 'whatsappDetails')}
                    placeholder="Approx. members"
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select
                    name="category"
                    value={formData.whatsappDetails.category}
                    onChange={(e) => handleChange(e, 'whatsappDetails')}
                  >
                    <option value="general">General</option>
                    <option value="housing">Housing</option>
                    <option value="rides">Rides</option>
                    <option value="jobs">Jobs/Internships</option>
                    <option value="food">Food</option>
                    <option value="events">Events</option>
                    <option value="study">Study Groups</option>
                    <option value="sports">Sports</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Contact Info */}
          <div className="contact-fields">
            <h4>Contact Information (Optional)</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.contactInfo.phone}
                  onChange={(e) => handleChange(e, 'contactInfo')}
                  placeholder="(555) 555-5555"
                />
              </div>
              <div className="form-group">
                <label>WhatsApp</label>
                <input
                  type="tel"
                  name="whatsapp"
                  value={formData.contactInfo.whatsapp}
                  onChange={(e) => handleChange(e, 'contactInfo')}
                  placeholder="+1 555 555 5555"
                />
              </div>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Posting...' : 'Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ISACommunity;
